/**
 * Negyedórás mérések egységes olvasása – működik a régi és a v2 kollekcióval is.
 *
 * Régi (energy_pods):   {pod}/measurements/{doc}  – negyedóránként egy dokumentum {timestamp, consumption, reverse_feed}
 * Új   (energy_pods_v2): {pod}/daily_summaries/{YYYYMMDD} – naponta egy dokumentum, `values[96]` + `values_export[96]`
 *
 * Mindkettőből ugyanazt az EnergiaMeasurement-szerű listát adja (timestamp, consumption, reverse_feed),
 * így a hívó kód (grafikonok, indexált árazás, MSCONS-összehasonlítás) változatlan maradhat.
 */
import { collection, query, where, orderBy, limit, getDocs, documentId, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { POD_COLLECTION, POD_COLLECTION_IS_V2 } from '../config/collections';

export interface PodMeasurement {
  timestamp: Timestamp;
  consumption: number;
  reverse_feed: number;
  price_huf_kwh: number;
  cost_huf: number;
}

const ymd = (d: Date) =>
  `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;

/** Napi v2 dokumentum → negyedórás sorok (intervallum-kezdet szerint, helyi idő). */
function expandDaily(docId: string, data: any): PodMeasurement[] {
  const base: Date = data.date?.toDate ? data.date.toDate() : new Date(
    Number(docId.slice(0, 4)), Number(docId.slice(4, 6)) - 1, Number(docId.slice(6, 8)));
  const day = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const values: number[] = Array.isArray(data.values) ? data.values : [];
  const exp: number[] | null = Array.isArray(data.values_export) ? data.values_export : null;
  const out: PodMeasurement[] = [];
  for (let i = 0; i < values.length; i++) {
    const ts = new Date(day.getTime() + i * 15 * 60 * 1000);
    out.push({
      timestamp: Timestamp.fromDate(ts),
      consumption: Number(values[i]) || 0,
      reverse_feed: exp ? Number(exp[i]) || 0 : 0,
      price_huf_kwh: 0,
      cost_huf: 0,
    });
  }
  return out;
}

/**
 * Mérések egy időszakra (mindkét vég zárt). v2-ben napi dokumentumokból bontva.
 */
export async function getMeasurementsInRange(podId: string, start: Date, end: Date): Promise<PodMeasurement[]> {
  if (POD_COLLECTION_IS_V2) {
    const ref = collection(db, POD_COLLECTION, podId, 'daily_summaries');
    const q = query(ref, where(documentId(), '>=', ymd(start)), where(documentId(), '<=', ymd(end)), orderBy(documentId()));
    const snap = await getDocs(q);
    const rows: PodMeasurement[] = [];
    snap.forEach(d => rows.push(...expandDaily(d.id, d.data())));
    return rows.filter(r => {
      const t = r.timestamp.toDate();
      return t >= start && t <= end;
    });
  }
  const ref = collection(db, POD_COLLECTION, podId, 'measurements');
  const q = query(ref, where('timestamp', '>=', Timestamp.fromDate(start)),
    where('timestamp', '<=', Timestamp.fromDate(end)), orderBy('timestamp', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const x = d.data();
    return {
      timestamp: x.timestamp, consumption: Number(x.consumption) || 0,
      reverse_feed: Number(x.reverse_feed) || 0, price_huf_kwh: Number(x.price_huf_kwh) || 0, cost_huf: Number(x.cost_huf) || 0,
    };
  });
}

/**
 * Legutóbbi N mérés, legújabb elöl (a POD-oldal grafikonja). v2-ben ceil(N/96) napi dokumentumból.
 */
export async function getLatestMeasurements(podId: string, limitCount: number): Promise<PodMeasurement[]> {
  if (POD_COLLECTION_IS_V2) {
    const days = Math.max(1, Math.ceil(limitCount / 96));
    const ref = collection(db, POD_COLLECTION, podId, 'daily_summaries');
    const q = query(ref, orderBy(documentId(), 'desc'), limit(days));
    const snap = await getDocs(q);
    const rows: PodMeasurement[] = [];
    snap.forEach(d => rows.push(...expandDaily(d.id, d.data())));
    rows.sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis());
    return rows.slice(0, limitCount);
  }
  const ref = collection(db, POD_COLLECTION, podId, 'measurements');
  const q = query(ref, orderBy('timestamp', 'desc'), limit(limitCount));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as PodMeasurement);
}

/** Az összes mérés, legújabb elöl. v2-ben az összes napi dokumentum kibontva. */
export async function getAllMeasurements(podId: string): Promise<PodMeasurement[]> {
  if (POD_COLLECTION_IS_V2) {
    const ref = collection(db, POD_COLLECTION, podId, 'daily_summaries');
    const snap = await getDocs(query(ref, orderBy(documentId(), 'desc')));
    const rows: PodMeasurement[] = [];
    snap.forEach(d => rows.push(...expandDaily(d.id, d.data())));
    rows.sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis());
    return rows;
  }
  const ref = collection(db, POD_COLLECTION, podId, 'measurements');
  const snap = await getDocs(query(ref, orderBy('timestamp', 'desc')));
  return snap.docs.map(d => d.data() as PodMeasurement);
}

/**
 * Havi összegek {YYYY-MM: {consumption, reverse_feed}} – v2-ben a monthly/ alkollekcióból (olcsó),
 * régiben a measurements összeadásával.
 */
export async function getMonthlyTotals(podId: string): Promise<Record<string, { consumption: number; reverse_feed: number }>> {
  const out: Record<string, { consumption: number; reverse_feed: number }> = {};
  if (POD_COLLECTION_IS_V2) {
    const snap = await getDocs(collection(db, POD_COLLECTION, podId, 'monthly'));
    snap.forEach(d => {
      const x = d.data();
      out[d.id] = { consumption: Number(x.total_consumption_kwh) || 0, reverse_feed: Number(x.total_reverse_feed_kwh) || 0 };
    });
    return out;
  }
  const rows = await getAllMeasurements(podId);
  for (const r of rows) {
    const t = r.timestamp.toDate();
    const k = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`;
    const e = out[k] || (out[k] = { consumption: 0, reverse_feed: 0 });
    e.consumption += r.consumption;
    e.reverse_feed += r.reverse_feed;
  }
  return out;
}
